import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, asyncScheduler, of, throwError } from 'rxjs';
import { catchError, finalize, map, observeOn, shareReplay, switchMap, tap } from 'rxjs/operators';

import { BrandUpdatedEvent, UserLoggedOutEvent } from '../models/events';
import { ApiService } from './api.service';
import { DriveSurfaceService } from './drive-surface.service';
import { EmitterService } from './emitter.service';
import { EntityItemNameResolverService } from './entity-item-name-resolver.service';
import { OverlayService } from './overlayService';
import { TranslatePipe } from '../pipes/translate.pipe';

export interface SignatureResponse {
  access_token?: string;
  expires_in?: number;
}

export interface SignatureRequestResult {
  signature: SignatureResponse;
  itemName: string;
}

interface SignatureCacheEntry {
  signature: SignatureResponse;
  itemName: string;
  expiresAt: number;
}

const SAFETY_MARGIN_MS = 30_000;

/**
 * Entity types backed by drive storage. On a drive surface these resolve their signer/participants
 * from the drive hierarchy, so the backend needs drive:true — including for My Own Space items,
 * which have no owning project (STUDIO-1998). The same type in a legacy project is a local rbac
 * entity and must not carry the flag, so the surface is checked too (see DriveSurfaceService).
 */
const DRIVE_STORAGE_ENTITY_TYPES: ReadonlySet<string> = new Set<string>([
  'SECTION',
  'SUB_SECTION',
  'FOLDER',
  'SUB_FOLDER',
  'VERSION',
  'FILE',
]);

@Injectable({ providedIn: 'root' })
export class PlatformRbacSignatureService {
  private readonly api = inject(ApiService);
  private readonly nameResolver = inject(EntityItemNameResolverService);
  private readonly driveSurface = inject(DriveSurfaceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly overlay = inject(OverlayService);
  private readonly translate = inject(TranslatePipe);

  private readonly cache = new Map<string, SignatureCacheEntry>();
  private readonly inFlight = new Map<string, Observable<SignatureRequestResult>>();
  private lastBrandId: string | null = null;

  constructor() {
    EmitterService.get(BrandUpdatedEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((brand: { id?: string } | null) => {
        const nextId: string | null = brand?.id ?? null;
        if (this.lastBrandId !== null && nextId !== this.lastBrandId) {
          this.clearCache();
        }
        this.lastBrandId = nextId;
      });
    EmitterService.get(UserLoggedOutEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.clearCache();
        this.lastBrandId = null;
      });
  }

  public requestSignature(
    entityType: string,
    entityUuid: string,
    entityName?: string,
    projectId?: string | null
  ): Observable<SignatureRequestResult> {
    const key: string = this.buildKey(entityType, entityUuid);

    const cached: SignatureCacheEntry | undefined = this.cache.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      // Emit asynchronously so subscribers (e.g. image-annotation in ngOnInit)
      // don't initialize plugins against a not-yet-rendered view.
      return of({ signature: cached.signature, itemName: cached.itemName }).pipe(observeOn(asyncScheduler));
    }
    if (cached) {
      this.cache.delete(key);
    }

    const inFlight: Observable<SignatureRequestResult> | undefined = this.inFlight.get(key);
    if (inFlight) {
      return inFlight;
    }

    // STUDIO-1808: drive-storage entities set drive:true so the backend resolves signer/participants
    // and skips the rbac sign mutation. A TASK carries projectId only to scope its signature but is
    // a local rbac entity, not a drive-storage item — exclude it.
    //
    // STUDIO-1998: keyed off entityType rather than the presence of a projectId, so My Own Space
    // FOLDER/FILE entities (no Project/Brand/Workspace ancestry, hence no projectId) still get the
    // flag. That dropped the only thing separating them from legacy-project entities: every FILE
    // then claimed to be a drive item, and legacy ones died backend-side on
    // "Cannot resolve owning project" (INVALID_PARAMETER), taking the comments panel with them.
    // The type must therefore be paired with the surface — drive project or My Own Space, never
    // a legacy project.
    const drive: boolean = DRIVE_STORAGE_ENTITY_TYPES.has(entityType) && this.driveSurface.isDriveSurface();

    const request$: Observable<SignatureRequestResult> = this.nameResolver
      .resolve(entityType, entityUuid, entityName ?? '')
      .pipe(
        switchMap((itemName: string) =>
          this.api
            .httpPost('/api/signature/request', {
              entityType,
              itemUuid: entityUuid,
              itemName: itemName || '',
              ...(projectId ? { projectId } : {}),
              ...(drive ? { drive: true } : {}),
            })
            .pipe(map((signature: SignatureResponse) => ({ signature, itemName })))
        ),
        tap((result: SignatureRequestResult) => this.store(key, result)),
        catchError((err) => {
          this.inFlight.delete(key);
          if (err?.status === 410) {
            // Item was deleted — surface it once at this shared choke point (STUDIO-1808).
            this.overlay.showWarning(this.translate.transform('signature_itemGone'));
          }
          return throwError(() => err);
        }),
        finalize(() => this.inFlight.delete(key)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.inFlight.set(key, request$);
    return request$;
  }

  public clearCache(entityType?: string, entityUuid?: string): void {
    if (entityType && entityUuid) {
      this.cache.delete(this.buildKey(entityType, entityUuid));
      return;
    }
    this.cache.clear();
  }

  private buildKey(entityType: string, entityUuid: string): string {
    return `${entityType}:${entityUuid}`;
  }

  private store(key: string, result: SignatureRequestResult): void {
    const expiresIn: number = result.signature?.expires_in ?? 0;
    if (!result.signature?.access_token || expiresIn <= 0) {
      return;
    }
    const ttlMs: number = expiresIn * 1000 - SAFETY_MARGIN_MS;
    if (ttlMs <= 0) {
      return;
    }
    this.cache.set(key, {
      signature: result.signature,
      itemName: result.itemName,
      expiresAt: Date.now() + ttlMs,
    });
  }
}
